/**
 * Renders policy content (HTML or Markdown) into safe HTML for display in
 * .vrd-policy-viewer__content. Detects format and either sanitizes HTML or
 * converts Markdown to HTML.
 */

const ALLOWED_TAGS = new Set([
  'p', 'div', 'span', 'br', 'hr',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li',
  'a', 'strong', 'b', 'em', 'i', 'u', 'code', 'pre', 'blockquote',
]);

const ALLOWED_ATTRS: Record<string, Set<string>> = {
  a: new Set(['href', 'target', 'rel']),
};

function escapeHtml(text: string): string {
  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  };
  return text.replace(/[&<>"']/g, (ch) => map[ch] ?? ch);
}

function looksLikeHtml(content: string): boolean {
  const trimmed = content.trim();
  return /^</.test(trimmed) || /<\/(?:p|div|span|h[1-6]|ul|ol|li|a|strong|em|code|pre|blockquote)\s*>/i.test(trimmed);
}

/**
 * Sanitize HTML: keep only allowed tags and safe attributes.
 */
function sanitizeHtml(html: string): string {
  if (typeof document === 'undefined' || !document.createElement) {
    return escapeHtml(html);
  }
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const walk = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      return escapeHtml(node.textContent ?? '');
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return '';
    const el = node as Element;
    const tag = el.tagName.toLowerCase();
    if (!ALLOWED_TAGS.has(tag)) {
      return Array.from(node.childNodes).map(walk).join('');
    }
    if (tag === 'script' || tag === 'style' || tag === 'iframe') return '';
    let attrs = '';
    const allowedForTag = ALLOWED_ATTRS[tag];
    if (allowedForTag) {
      for (const { name, value } of Array.from(el.attributes)) {
        const n = name.toLowerCase();
        if (!allowedForTag.has(n)) continue;
        if (n === 'href' && /^\s*javascript\s*:/i.test(value)) continue;
        if (n === 'on' || n.startsWith('on')) continue;
        attrs += ` ${n}="${escapeHtml(value)}"`;
      }
    }
    const inner = Array.from(node.childNodes).map(walk).join('');
    if (tag === 'br' || tag === 'hr') return `<${tag}${attrs}>`;
    return `<${tag}${attrs}>${inner}</${tag}>`;
  };
  return Array.from(doc.body.childNodes).map(walk).join('');
}

/**
 * Convert basic Markdown to HTML. Escapes raw HTML so content is safe.
 */
function markdownToHtml(md: string): string {
  let s = escapeHtml(md);
  // Code blocks (fenced)
  s = s.replace(/^```(\w*)\n([\s\S]*?)```/gm, (_m, _lang, code) =>
    `<pre><code>${(code as string).replace(/\n/g, '<br>')}</code></pre>`
  );
  // Headers (at line start)
  s = s.replace(/^######\s+(.+)$/gm, '<h6>$1</h6>');
  s = s.replace(/^#####\s+(.+)$/gm, '<h5>$1</h5>');
  s = s.replace(/^####\s+(.+)$/gm, '<h4>$1</h4>');
  s = s.replace(/^###\s+(.+)$/gm, '<h3>$1</h3>');
  s = s.replace(/^##\s+(.+)$/gm, '<h2>$1</h2>');
  s = s.replace(/^#\s+(.+)$/gm, '<h1>$1</h1>');
  // Bold and italic
  s = s.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  s = s.replace(/\*(.+?)\*/g, '<em>$1</em>');
  s = s.replace(/__(.+?)__/g, '<strong>$1</strong>');
  s = s.replace(/_(.+?)_/g, '<em>$1</em>');
  // Inline code
  s = s.replace(/`([^`]+)`/g, '<code>$1</code>');
  // Links [text](url)
  s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
  // Unordered list: lines starting with - or *
  s = s.replace(/^\s*[-*]\s+(.+)$/gm, '<li>$1</li>');
  // Ordered list: lines starting with N.
  s = s.replace(/^\s*\d+\.\s+(.+)$/gm, '<li>$1</li>');
  // Wrap consecutive <li> in <ul>
  s = s.replace(/(<li>.*?<\/li>\n?)+/gs, (m) => `<ul>${m}</ul>`);
  // Paragraphs: wrap runs of non-tag lines in <p>, join with <br>
  const lines = s.split(/\n/);
  const out: string[] = [];
  const blockStart = /^<(h[1-6]|ul|ol|li|pre|blockquote)\b/;
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const t = line.trim();
    if (t === '') {
      i++;
      continue;
    }
    if (blockStart.test(t)) {
      out.push(line);
      i++;
      continue;
    }
    const para: string[] = [];
    while (i < lines.length) {
      const L = lines[i];
      const T = L.trim();
      if (T === '' || blockStart.test(T)) break;
      para.push(L);
      i++;
    }
    if (para.length) out.push(`<p>${para.join('<br>\n')}</p>`);
  }
  return out.join('\n');
}

/**
 * Render policy content string to safe HTML for display.
 * If content looks like HTML, it is sanitized; otherwise it is treated as Markdown.
 */
export function renderPolicyContentToHtml(content: string): string {
  const raw = (content || '').trim();
  if (!raw) return '<p>No content.</p>';
  if (looksLikeHtml(raw)) return sanitizeHtml(raw);
  return markdownToHtml(raw);
}
